import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { withRouter, useHistory } from 'react-router-dom'
import Header from '../header/Header'
import { routes } from '../../constantes/routes'
import { articleSearchApi } from '../../api/articleSearchApi'
import { articlesApi } from '../../api/articlesApi'
import { API } from '../../api/comunApi'

/**
 * Banco de pruebas de los dos motores de búsqueda.
 *
 * Pantalla independiente en /buscador-comparar. No toca ni el Catálogo ni el
 * BackOffice: se llega sólo por URL, así que ningún usuario tropieza con ella
 * y el riesgo para lo que ya funciona es cero.
 *
 * IMPORTANTE sobre el panel izquierdo: reproduce las DOS llamadas de servidor
 * que hace hoy el Catálogo (productos finales + carpetas), no el recorrido
 * recursivo por el árbol. Eso son 485 peticiones más y no aportaría nada a la
 * comparación de motores; su coste ya está medido aparte.
 */

const COL = {
  borde: '1px solid #dce2e8',
  fondo: '#fff',
}

// ---------------------------------------------------------------- utilidades

const useDebounce = (valor, ms) => {
  const [retardado, setRetardado] = useState(valor)

  useEffect(() => {
    const t = setTimeout(() => setRetardado(valor), ms)
    return () => clearTimeout(t)
  }, [valor, ms])

  return retardado
}

/**
 * Saca el mensaje útil de un error de axios.
 *
 * Con ASPNETCORE_ENVIRONMENT=Development, un 500 devuelve la página de
 * excepción del desarrollador en HTML. El tipo y el mensaje de la excepción
 * están en un div con clase "titleerror". Sin esto, la pantalla sólo dice
 * "ha fallado con 500" y hay que ir a mirar a la pestaña Network.
 */
const extraerError = (err) => {
  const codigo = err && err.response ? err.response.status : null
  const datos = err && err.response ? err.response.data : null

  const detalle = (() => {
    if (typeof datos === 'string') {
      const titulo = datos.match(/class="titleerror">([\s\S]*?)<\/div>/)
      if (titulo) return titulo[1].replace(/<[^>]+>/g, '').trim()

      const h1 = datos.match(/<h1[^>]*>([\s\S]*?)<\/h1>/)
      if (h1) return h1[1].replace(/<[^>]+>/g, '').trim()

      return datos.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 400)
    }
    if (datos && typeof datos === 'object') {
      return datos.mensaje || datos.message || JSON.stringify(datos).slice(0, 400)
    }
    return err && err.message ? err.message : ''
  })()

  // Las líneas de la pila que pasan por nuestro código son las que localizan
  // el fallo. El resto (EF6, ASP.NET) sólo estorba.
  const marcoNuestro =
    typeof datos === 'string'
      ? (datos.match(/Palex\.[A-Za-z0-9_.&lt;&gt;<>`+]+/g) || [])
          .map((m) => m.replace(/&lt;/g, '<').replace(/&gt;/g, '>'))
          .filter((m, i, arr) => arr.indexOf(m) === i)
          .slice(0, 6)
      : []

  return {
    codigo: codigo || '—',
    detalle: marcoNuestro.length
      ? `${detalle}\n\nen:\n  ${marcoNuestro.join('\n  ')}`
      : detalle,
  }
}

const Campo = ({ etiqueta, valor, onChange, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    <label
      style={{
        display: 'block',
        fontSize: 11,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#5c6773',
        marginBottom: 4,
      }}
    >
      {etiqueta}
    </label>
    <input
      className='btn-filter search placeholder-text'
      style={{ width: '100%' }}
      type='text'
      value={valor}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
)

const Estado = ({ cargando, error, total, ms, extra }) => {
  if (cargando) return <span style={{ color: '#5c6773' }}>Buscando…</span>
  if (error) {
    return (
      <div
        style={{
          color: '#a32218',
          background: '#f6e3e1',
          borderLeft: '3px solid #a32218',
          padding: '8px 11px',
          fontSize: 12.5,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 160,
          overflowY: 'auto',
        }}
      >
        {error}
      </div>
    )
  }
  return (
    <span style={{ color: '#5c6773' }}>
      <strong style={{ color: '#151a20' }}>{total}</strong> resultados
      {ms !== null ? ` · ${ms} ms` : ''}
      {extra ? ` · ${extra}` : ''}
    </span>
  )
}

const Lista = ({ items }) => (
  <ol
    style={{
      margin: 0,
      paddingLeft: 22,
      maxHeight: 460,
      overflowY: 'auto',
      fontSize: 13,
      lineHeight: 1.55,
    }}
  >
    {items.map((it) => (
      <li key={it.id_Articulo} style={{ marginBottom: 3 }}>
        <code style={{ color: '#1b5e7e' }}>{it.referencia || '—'}</code>{' '}
        {it.nombre}
        {it.isArticuloFinal === false ? (
          <span style={{ color: '#8b97a3' }}> · carpeta</span>
        ) : null}
        {it.matchEn && it.matchEn.length ? (
          <span style={{ color: '#8b97a3' }}> · {it.matchEn.join('+')}</span>
        ) : null}
      </li>
    ))}
  </ol>
)

/**
 * Contra qué API está hablando esta pantalla.
 *
 * CRA inyecta las variables de .env al arrancar el servidor de desarrollo: si
 * se edita .env.development.local con npm start ya en marcha, el cambio no se
 * aplica hasta relanzarlo, y el frontend sigue apuntando a producción sin
 * avisar. Eso hace que el motor v2 devuelva 404 (producción no lo tiene) y
 * cuesta un rato de averiguar. Aquí se ve de un vistazo.
 */
const BannerApi = () => {
  const base = API.defaults.baseURL || '(relativa)'
  const esLocal = base.indexOf('localhost') !== -1 || base.indexOf('127.0.0.1') !== -1

  return (
    <div
      style={{
        fontSize: 13,
        padding: '9px 13px',
        margin: '0 0 16px',
        background: esLocal ? '#e2efe9' : '#f6e3e1',
        borderLeft: `3px solid ${esLocal ? '#1b6b4c' : '#a32218'}`,
      }}
    >
      API: <code>{base}</code>
      {esLocal ? null : (
        <span style={{ color: '#a32218', fontWeight: 600 }}>
          {' '}
          — no es local. Si v2 devuelve 404 es por esto: relanza el servidor de
          desarrollo del frontend para que lea <code>.env.development.local</code>.
        </span>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ pantalla

const SearchCompareContainer = () => {
  const history = useHistory()

  const [scope, setScope] = useState('all')

  // Los dos buscadores son independientes, cada uno con su propio input.
  const [terminoLegacy, setTerminoLegacy] = useState('')
  const [terminoV2, setTerminoV2] = useState('')
  const [sincronizar, setSincronizar] = useState(true)

  const debLegacy = useDebounce(terminoLegacy, 350)
  const debV2 = useDebounce(terminoV2, 350)

  const [legacy, setLegacy] = useState({ items: [], ms: null, cargando: false, error: null })
  const [v2, setV2] = useState({ items: [], total: 0, ms: null, cargando: false, error: null })
  const [diff, setDiff] = useState(null)
  const [diffCargando, setDiffCargando] = useState(false)

  const escribirLegacy = useCallback(
    (valor) => {
      setTerminoLegacy(valor)
      if (sincronizar) setTerminoV2(valor)
    },
    [sincronizar],
  )

  const escribirV2 = useCallback(
    (valor) => {
      setTerminoV2(valor)
      if (sincronizar) setTerminoLegacy(valor)
    },
    [sincronizar],
  )

  // ---- motor antiguo
  useEffect(() => {
    const fuente = axios.CancelToken.source()
    const t0 = performance.now()
    setLegacy((s) => ({ ...s, cargando: true, error: null }))

    const busqueda = debLegacy.trim() === '' ? 'none' : debLegacy.trim()

    Promise.all([
      scope === 'folder'
        ? Promise.resolve({ data: [] })
        : articlesApi.getAllFinalProductsBySearch(busqueda, 1, ''),
      scope === 'final'
        ? Promise.resolve({ data: [] })
        : articlesApi.getAllFolderProductsBySearch(busqueda, 1, ''),
    ])
      .then(([finales, carpetas]) => {
        const items = [
          ...(finales.data || []).map((x) => ({ ...x, isArticuloFinal: true })),
          ...(carpetas.data || []).map((x) => ({ ...x, isArticuloFinal: false })),
        ]
        setLegacy({
          items,
          ms: Math.round(performance.now() - t0),
          cargando: false,
          error: null,
        })
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        const { codigo, detalle } = extraerError(err)
        setLegacy({
          items: [],
          ms: Math.round(performance.now() - t0),
          cargando: false,
          error:
            `Motor actual — HTTP ${codigo}\n` +
            (detalle || 'El término viaja crudo en la ruta de la URL.'),
        })
      })

    return () => fuente.cancel()
  }, [debLegacy, scope])

  // ---- motor v2
  useEffect(() => {
    const fuente = axios.CancelToken.source()
    const t0 = performance.now()
    setV2((s) => ({ ...s, cargando: true, error: null }))

    articleSearchApi
      .search({ q: debV2, scope, page: 1, pageSize: 50 }, fuente.token)
      .then((res) => {
        setV2({
          items: res.data.items || [],
          total: res.data.total,
          ms: Math.round(performance.now() - t0),
          cargando: false,
          error: null,
        })
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        const { codigo, detalle } = extraerError(err)
        setV2({
          items: [],
          total: 0,
          ms: Math.round(performance.now() - t0),
          cargando: false,
          error: `v2 — HTTP ${codigo}\n${detalle}`,
        })
      })

    return () => fuente.cancel()
  }, [debV2, scope])

  const comparar = () => {
    setDiffCargando(true)
    articleSearchApi
      .compare({ q: debV2, scope })
      .then((res) => {
        setDiff(res.data)
        setDiffCargando(false)
      })
      .catch(() => {
        setDiff(null)
        setDiffCargando(false)
      })
  }

  return (
    <div className='products'>
      <Header
        text={'Comparador de motores de búsqueda'}
        route={() => history.push(routes.MAIN)}
        backBtn={true}
      />

      <BannerApi />

      <div style={{ display: 'flex', gap: 20, alignItems: 'center', margin: '0 0 20px' }}>
        <div>
          <label
            style={{
              display: 'block',
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#5c6773',
              marginBottom: 4,
            }}
          >
            Ámbito
          </label>
          <select
            className='btn-filter'
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value='all'>Todo</option>
            <option value='final'>Sólo productos finales</option>
            <option value='folder'>Sólo familias / subfamilias</option>
          </select>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 18 }}>
          <input
            type='checkbox'
            checked={sincronizar}
            onChange={(e) => setSincronizar(e.target.checked)}
          />
          <span style={{ fontSize: 14 }}>Escribir en los dos a la vez</span>
        </label>

        <button
          className='action-button'
          style={{ marginTop: 18 }}
          onClick={comparar}
          disabled={diffCargando}
        >
          {diffCargando ? 'Comparando…' : 'Comparar en el servidor'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* -------- motor antiguo -------- */}
        <div style={{ border: COL.borde, background: COL.fondo, padding: 16 }}>
          <h3 style={{ marginTop: 0, fontSize: 17 }}>Motor actual</h3>
          <p style={{ fontSize: 12.5, color: '#5c6773', marginBottom: 12 }}>
            Las dos llamadas de servidor que hace hoy el Catálogo. No incluye el
            recorrido recursivo del árbol (485 peticiones más).
          </p>
          <Campo
            etiqueta='Buscar'
            valor={terminoLegacy}
            onChange={escribirLegacy}
            placeholder='aguja'
          />
          <div style={{ marginBottom: 10 }}>
            <Estado
              cargando={legacy.cargando}
              error={legacy.error}
              total={legacy.items.length}
              ms={legacy.ms}
              extra={legacy.items.length === 40 || legacy.items.length === 20 ? 'posible truncado' : null}
            />
          </div>
          <Lista items={legacy.items} />
        </div>

        {/* -------- motor v2 -------- */}
        <div style={{ border: COL.borde, background: COL.fondo, padding: 16 }}>
          <h3 style={{ marginTop: 0, fontSize: 17 }}>Motor v2</h3>
          <p style={{ fontSize: 12.5, color: '#5c6773', marginBottom: 12 }}>
            Una sola llamada. Devuelve el total real y explica por qué ha
            entrado cada resultado.
          </p>
          <Campo
            etiqueta='Buscar'
            valor={terminoV2}
            onChange={escribirV2}
            placeholder='aguja'
          />
          <div style={{ marginBottom: 10 }}>
            <Estado
              cargando={v2.cargando}
              error={v2.error}
              total={v2.total}
              ms={v2.ms}
              extra={`mostrando ${v2.items.length}`}
            />
          </div>
          <Lista items={v2.items} />
        </div>
      </div>

      {/* -------- diferencias -------- */}
      {diff ? (
        <div style={{ border: COL.borde, background: COL.fondo, padding: 16, marginTop: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 17 }}>Diferencias</h3>

          <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginBottom: 14, fontSize: 14 }}>
            <span>
              En ambos: <strong>{diff.enAmbos}</strong>
            </span>
            <span style={{ color: diff.soloLegacy.length ? '#a32218' : '#1b6b4c' }}>
              Sólo motor actual: <strong>{diff.soloLegacy.length}</strong>
              {diff.soloLegacy.length === 0 ? ' ✓' : ' ⚠'}
            </span>
            <span>
              Sólo v2: <strong>{diff.soloV2.length}</strong>
            </span>
            <span style={{ color: '#5c6773' }}>
              {diff.legacy.ms} ms vs {diff.v2.ms} ms
            </span>
          </div>

          {diff.avisos.map((aviso) => (
            <p
              key={aviso}
              style={{
                fontSize: 13.5,
                background: '#f3eeda',
                borderLeft: '3px solid #6e5a00',
                padding: '9px 12px',
                margin: '0 0 8px',
              }}
            >
              {aviso}
            </p>
          ))}

          <p style={{ fontSize: 13, color: '#5c6773', margin: '14px 0 10px' }}>
            El criterio no es que no haya diferencias: si las dos listas
            coincidieran, v2 habría reproducido los fallos del motor antiguo.
            El criterio es que <strong>“sólo motor actual” esté vacío</strong> y
            que cada entrada de “sólo v2” tenga explicación.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <h4 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#a32218' }}>
                Sólo motor actual ({diff.soloLegacy.length})
              </h4>
              {diff.soloLegacy.length ? (
                <Lista items={diff.soloLegacy} />
              ) : (
                <p style={{ fontSize: 13, color: '#1b6b4c' }}>
                  Vacío. v2 no pierde nada de lo que encuentra el motor actual.
                </p>
              )}
            </div>
            <div>
              <h4 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1b5e7e' }}>
                Sólo v2 ({diff.soloV2.length})
              </h4>
              <Lista items={diff.soloV2} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default withRouter(SearchCompareContainer)
